import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as actions from "../../../../../actions/index";
import { EDITSUBSCRIPTION_RESPONSE } from "../../../../../constants/action_types";
import EditPlan from "./edit-plan";
import EditAddOns from "./edit-addons";
import { MonthlyTotalBar } from "../components";

// ── Billing cycle helpers ───────────────────────────────────────────────────

const BILLING_CYCLE_MAP = {
  monthly:   { period: 1, period_unit: "month" },
  quarterly: { period: 3, period_unit: "month" },
  annually:  { period: 1, period_unit: "year" },
};

function getBillingCycleKey(period, period_unit) {
  if (!period_unit) return "monthly";
  if (period_unit === "year") return "annually";
  if (period === 3 && period_unit === "month") return "quarterly";
  return "monthly";
}

function findMatchingPrice(plan_prices, billingCycle) {
  if (!plan_prices || !plan_prices.length) return null;
  const { period, period_unit } = BILLING_CYCLE_MAP[billingCycle] || BILLING_CYCLE_MAP.monthly;
  return plan_prices.find((p) => p.period === period && p.period_unit === period_unit) || null;
}

// ── Totals ──────────────────────────────────────────────────────────────────

function calcTotals(billingCycle, seats, pricePerSeat, checkedAddons, items, addonOverrides, planPricingModel) {
  if (!items) return { plan: 0, addons: 0, total: 0 };

  const allAddons = items.filter((i) => i.plan_type === "addon");
  const planTotal = pricePerSeat != null
    ? (planPricingModel === "per_unit" ? pricePerSeat * seats : pricePerSeat)
    : 0;

  let addonsTotal = 0;
  for (const name of checkedAddons) {
    const addon = allAddons.find((a) => a.plan_name === name);
    if (!addon) continue;
    const p = findMatchingPrice(addon.plan_prices, billingCycle);
    if (!p) continue;
    const override   = addonOverrides[name] || {};
    const unitPrice  = override.unitPrice ?? p.price;
    const qty        = override.qty ?? (p.pricing_model === "per_unit" ? seats : 1);
    addonsTotal += p.pricing_model === "per_unit" ? unitPrice * qty : unitPrice;
  }

  return { plan: planTotal, addons: addonsTotal, total: planTotal + addonsTotal };
}

// ── Build save payload ──────────────────────────────────────────────────────

function buildPayload(selectedPlanName, billingCycle, seats, pricePerSeat, checkedAddons, items, addonOverrides, currentSubscription) {
  if (!items) return null;

  const allPlans  = items.filter((i) => i.plan_type === "plan");
  const allAddons = items.filter((i) => i.plan_type === "addon");

  const plan      = allPlans.find((p) => p.plan_name === selectedPlanName);
  const planPrice = plan ? findMatchingPrice(plan.plan_prices, billingCycle) : null;
  const amount    = pricePerSeat ?? 0;

  // Never go below active users (no_of_users), but min_no_of_users can be overridden
  const effectiveSeats = Math.max(seats, currentSubscription?.no_of_users || 0);

  const addons = [];
  for (const name of checkedAddons) {
    const addon = allAddons.find((a) => a.plan_name === name);
    if (!addon) continue;
    const p = findMatchingPrice(addon.plan_prices, billingCycle);
    if (!p) continue;
    const override  = addonOverrides[name] || {};
    const unitPrice = override.unitPrice ?? p.price;
    const enteredQty = override.qty ?? (p.pricing_model === "per_unit" ? seats : 1);
    // Never go below the addon's current active quantity
    const currentAddon = (currentSubscription?.addons || []).find(
      (a) => a.item_price_id === p.plan_price_id
    );
    const qty = p.pricing_model === "per_unit"
      ? Math.max(enteredQty, currentAddon?.quantity || 0)
      : enteredQty;
    addons.push({
      item_price_id: p.plan_price_id,
      unit_price: unitPrice,
      quantity: p.pricing_model === "per_unit" ? qty : 1,
      ...(p.pricing_model === "per_unit" ? { min_quantity: qty } : {}),
    });
  }

  return {
    plan_price_id: planPrice?.plan_price_id,
    amount,
    max_users: currentSubscription?.max_users ?? effectiveSeats,
    min_users: effectiveSeats,
    auto_collection: false,
    addons,
  };
}

// ── Component ───────────────────────────────────────────────────────────────

function EditSubscription({ company_uid, onClose }) {
  const dispatch = useDispatch();

  const subscriptions      = useSelector((state) => state.company.subscriptions);
  const rawSubscriptionItems = useSelector((state) => state.company.subscription_items);
  const subscriptionItems  = Array.isArray(rawSubscriptionItems) ? rawSubscriptionItems : null;
  const updateResponse     = useSelector((state) => state.company.update_response);

  const [selectedPlanName, setSelectedPlanName] = useState(null);
  const [billingCycle, setBillingCycle]         = useState("monthly");
  const [seats, setSeats]                       = useState(1);
  const [pricePerSeat, setPricePerSeat]         = useState(null);
  const [checkedAddons, setCheckedAddons]       = useState(new Set());
  const [addonOverrides, setAddonOverrides]     = useState({});
  const [searchQuery, setSearchQuery]           = useState("");
  const [isSaving, setIsSaving]                 = useState(false);
  const [isDirty, setIsDirty]                   = useState(false);

  const initialRef = useRef(null);

  // Pre-populate from current subscription once items are loaded
  useEffect(() => {
    if (!subscriptions || !subscriptionItems) return;

    const allPlans  = subscriptionItems.filter((i) => i.plan_type === "plan");
    const allAddons = subscriptionItems.filter((i) => i.plan_type === "addon");
    const currentPlanId = subscriptions.plan_id;

    let initPlanName = null;
    let initCycle    = "monthly";

    for (const plan of allPlans) {
      const match = plan.plan_prices.find((p) => p.plan_price_id === currentPlanId);
      if (match) {
        initPlanName = plan.plan_name;
        initCycle    = getBillingCycleKey(match.period, match.period_unit);
        break;
      }
    }

    const currentAddonIds = new Set((subscriptions.addons || []).map((a) => a.item_price_id));
    const initChecked = new Set();
    for (const addon of allAddons) {
      if (addon.plan_prices.some((p) => currentAddonIds.has(p.plan_price_id))) {
        initChecked.add(addon.plan_name);
      }
    }

    // Pre-populate per-addon qty and price from current subscription
    const initOverrides = {};
    for (const currentAddon of (subscriptions.addons || [])) {
      const plan = allAddons.find((a) =>
        a.plan_prices.some((p) => p.plan_price_id === currentAddon.item_price_id)
      );
      if (plan) {
        initOverrides[plan.plan_name] = {
          qty:       Math.max(currentAddon.min_quantity || 0, currentAddon.quantity || 0) || 1,
          unitPrice: currentAddon.unit_price,
        };
      }
    }

    const initSeats = Math.max(subscriptions.no_of_users || 0, subscriptions.min_no_of_users || 0) || 1;

    // Derive price from actual billing, respecting pricing model
    const currentPlanPrice      = allPlans.flatMap((p) => p.plan_prices).find((p) => p.plan_price_id === currentPlanId);
    const currentPricingModel   = currentPlanPrice?.pricing_model ?? null;
    const currentAddonsTotal    = (subscriptions.addons || []).reduce((sum, a) => sum + (a.amount || 0), 0);
    const currentPlanTotal      = subscriptions.cost != null ? subscriptions.cost - currentAddonsTotal : null;
    const initPricePerSeat      = currentPlanTotal != null
      ? (currentPricingModel === "per_unit" && initSeats
          ? Math.round((currentPlanTotal / initSeats) * 100) / 100
          : currentPlanTotal)
      : null;

    setSelectedPlanName(initPlanName);
    setBillingCycle(initCycle);
    setSeats(initSeats);
    setPricePerSeat(initPricePerSeat);
    setCheckedAddons(initChecked);
    setAddonOverrides(initOverrides);

    initialRef.current = JSON.stringify({
      plan:         initPlanName,
      cycle:        initCycle,
      seats:        initSeats,
      pricePerSeat: initPricePerSeat,
      addons:       [...initChecked].sort(),
      overrides:    initOverrides,
    });
  }, [subscriptions, subscriptionItems]);

  // Track dirty state
  useEffect(() => {
    if (!initialRef.current) return;
    const current = JSON.stringify({
      plan:         selectedPlanName,
      cycle:        billingCycle,
      seats,
      pricePerSeat,
      addons:       [...checkedAddons].sort(),
      overrides:    addonOverrides,
    });
    setIsDirty(current !== initialRef.current);
  }, [selectedPlanName, billingCycle, seats, pricePerSeat, checkedAddons, addonOverrides]);

  // Close modal on save success
  useEffect(() => {
    if (isSaving && updateResponse?.type === EDITSUBSCRIPTION_RESPONSE) {
      setIsSaving(false);
      onClose();
    }
  }, [updateResponse, isSaving]);

  // ESC key
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isDirty]);

  const handleClose = () => {
    if (isDirty) {
      if (window.confirm("You have unsaved changes. Are you sure you want to close?")) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleToggleAddon = (planName) => {
    setCheckedAddons((prev) => {
      const next = new Set(prev);
      if (next.has(planName)) next.delete(planName);
      else next.add(planName);
      return next;
    });
  };

  const handleAddonOverrideChange = (planName, field, value) => {
    setAddonOverrides((prev) => ({
      ...prev,
      [planName]: { ...prev[planName], [field]: value },
    }));
  };

  const handlePlanChange = (planName) => {
    setSelectedPlanName(planName);
    if (subscriptionItems) {
      const plan = subscriptionItems.filter((i) => i.plan_type === "plan").find((p) => p.plan_name === planName);
      const price = plan ? findMatchingPrice(plan.plan_prices, billingCycle) : null;
      setPricePerSeat(price?.price ?? null);
      const prevPlan = availablePlans.find((p) => p.plan_name === selectedPlanName);
      if (plan?.plan_family_id !== prevPlan?.plan_family_id) {
        setCheckedAddons(new Set());
        setAddonOverrides({});
      }
    }
  };

  const handleBillingCycleChange = (cycle) => {
    setBillingCycle(cycle);
    setSelectedPlanName(null);
    setPricePerSeat(null);
    setCheckedAddons(new Set());
    setAddonOverrides({});
  };

  const handleSave = () => {
    if (!subscriptionItems || !selectedPlanName) return;
    setIsSaving(true);
    const formData = buildPayload(selectedPlanName, billingCycle, seats, pricePerSeat, checkedAddons, subscriptionItems, addonOverrides, subscriptions);
    dispatch(actions.EDITSUBSCRIPTION_REQ({ company_uid, formData }));
  };

  const availablePlans  = subscriptionItems ? subscriptionItems.filter((i) => i.plan_type === "plan")  : [];
  const availableAddons = subscriptionItems ? subscriptionItems.filter((i) => i.plan_type === "addon") : [];
  const selectedPlanObj          = availablePlans.find((p) => p.plan_name === selectedPlanName) || null;
  const selectedPlanFamilyId     = selectedPlanObj?.plan_family_id ?? null;
  const selectedPlanMatchingPrice = selectedPlanObj ? findMatchingPrice(selectedPlanObj.plan_prices, billingCycle) : null;
  const selectedPlanPricingModel  = selectedPlanMatchingPrice?.pricing_model ?? null;
  const totals = calcTotals(billingCycle, seats, pricePerSeat, checkedAddons, subscriptionItems, addonOverrides, selectedPlanPricingModel);

  const PERIOD_LABEL = { monthly: "/mo", quarterly: "/qtr", annually: "/yr" };
  const periodLabel = PERIOD_LABEL[billingCycle] ?? "/mo";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-subscription-title"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} aria-hidden="true" />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0 bg-white">
          <div>
            <h2 id="edit-subscription-title" className="text-lg font-semibold text-gray-900">
              Edit Subscription
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">Modify plan and add-ons</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              className="text-gray-400" aria-hidden="true"
            >
              <path d="M18 6 6 18" /><path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto">
          {!subscriptionItems ? (
            <div className="animate-pulse p-5 space-y-4">
              <div className="h-24 bg-gray-200 rounded-xl" />
              <div className="h-48 bg-gray-200 rounded-xl" />
            </div>
          ) : (
            <div className="p-5 space-y-5">
              <EditPlan
                availablePlans={availablePlans}
                selectedPlanName={selectedPlanName}
                billingCycle={billingCycle}
                seats={seats}
                pricePerSeat={pricePerSeat}
                planPricingModel={selectedPlanPricingModel}
                onPlanChange={handlePlanChange}
                onBillingCycleChange={handleBillingCycleChange}
                onSeatsChange={setSeats}
                onPricePerSeatChange={setPricePerSeat}
              />
              <EditAddOns
                availableAddons={availableAddons}
                checkedAddons={checkedAddons}
                onToggle={handleToggleAddon}
                billingCycle={billingCycle}
                seats={seats}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                addonOverrides={addonOverrides}
                onAddonOverrideChange={handleAddonOverrideChange}
                planFamilyId={selectedPlanFamilyId}
              />
              {/* One-time charges — hidden until API available */}
              {/* <EditOneTime charges={charges} onChargesChange={setCharges} /> */}
            </div>
          )}
        </div>

        {/* Sticky Footer */}
        <MonthlyTotalBar
          variant="modal"
          total={totals.total}
          planAmount={totals.plan}
          addonsAmount={totals.addons}
          periodLabel={periodLabel}
          onCancel={handleClose}
          onSave={handleSave}
          isSaving={isSaving}
          isSaveDisabled={!selectedPlanName}
        />
      </div>
    </div>
  );
}

export default EditSubscription;
